import { defineComponent as _defineComponent } from 'vue'
import { resolveComponent as _resolveComponent, createVNode as _createVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createBlock as _createBlock, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, vModelText as _vModelText, normalizeClass as _normalizeClass, withDirectives as _withDirectives, withModifiers as _withModifiers, withCtx as _withCtx, normalizeStyle as _normalizeStyle } from "vue"

const _hoisted_1 = ["id"]
const _hoisted_2 = {
  key: 0,
  class: "c-unsubscribe__media"
}
const _hoisted_3 = { class: "c-unsubscribe__panel" }
const _hoisted_4 = { class: "c-unsubscribe__field-group" }
const _hoisted_5 = {
  key: 0,
  class: "c-unsubscribe__label"
}
const _hoisted_6 = { class: "c-unsubscribe__field-group" }
const _hoisted_7 = {
  key: 0,
  class: "c-unsubscribe__label"
}
const _hoisted_8 = { class: "c-unsubscribe__captcha" }
const _hoisted_9 = ["src"]
const _hoisted_10 = {
  key: 1,
  class: "c-unsubscribe__error"
}
const _hoisted_11 = { class: "c-unsubscribe__field-group" }
const _hoisted_12 = { class: "c-unsubscribe__label" }
const _hoisted_13 = { class: "c-unsubscribe__email-code" }
const _hoisted_14 = ["disabled"]
const _hoisted_15 = {
  key: 0,
  class: "c-unsubscribe__error"
}
const _hoisted_16 = ["disabled"]

import { computed, inject, onBeforeUnmount, onMounted, ref } from 'vue';
import DynamicForm from '@/form/DynamicForm.vue';
import SiteRichText from '@/elements/SiteRichText.vue';
import api from '@/api';
import utils from '@/utils';
import type { DynamicFormRef } from '@/types/form/dynamic-form';
import type { ToastProvider } from '@/types/layout';
import type { UnsubscribeData, UnsubscribeProps } from '@/types/components/Unsubscribe';


export default /*@__PURE__*/_defineComponent({
  __name: 'Unsubscribe',
  props: {
    dataSource: {},
    fields: {},
    rendering: {},
    params: {}
  },
  setup(__props: any) {

const props = __props;

const toast = inject<ToastProvider>('toast')!;
const formRef = ref<DynamicFormRef>();
const captchaValue = ref('');
const captchaError = ref('');
const emailCode = ref('');
const emailCodeError = ref('');
const feedbackMessage = ref('');
const feedbackType = ref<'success' | 'error'>('success');
const submitting = ref(false);
const sendingCode = ref(false);
const captchaLoading = ref(false);
const captchaImage = ref('');
const validateCode = ref('');
const codeToken = ref('');
let redirectTimer: ReturnType<typeof setTimeout> | undefined;

const fields = computed(() => props.fields);
const rootClass = computed(() => utils.overall.baseComClass(props.fields, 'c-unsubscribe'));
const rootStyle = computed(() => utils.overall?.baseComStyle?.(fields.value) || {});

const submitLabel = computed(() => fields.value?.submitLabel?.value || 'Submit');
const emailCodeLabel = computed(() => 'Please enter Email activation code');
const dynamicForm = computed(() => {
  const form = fields.value?.form;
  return Array.isArray(form) ? form[0] : form;
});
const feedbackClass = computed(() => `c-unsubscribe__feedback--${feedbackType.value}`);
const normalizedBackMedia = computed(() => {
  const backMedia = fields.value?.backMedia;
  return Array.isArray(backMedia) ? backMedia[0] : backMedia;
});

const normalizeCaptchaData = (data: any = {}) => {
  validateCode.value = data.validateCode || data.validatecode || '';
  codeToken.value = data.code_token || '';
  captchaImage.value = data.src || '';
};

const getErrorMessage = (error: any, fallback: string) => {
  return error?.response?.data?.message || error?.message || error?.data?.message || fallback;
};

const getResetToken = (data: any = {}) => {
  if (typeof data === 'string') {
    try {
      const parsed = JSON.parse(data);
      return parsed.reset_token || parsed.resetToken || '';
    } catch (error) {
      return '';
    }
  }

  return data.reset_token || data.resetToken || '';
};

const refreshCaptcha = async () => {
  if (captchaLoading.value) return;

  captchaLoading.value = true;
  captchaError.value = '';
  const [data, error] = await api.common.getValidateCode();
  captchaLoading.value = false;

  if (error) {
    captchaImage.value = '';
    captchaError.value = getErrorMessage(error, 'Failed to load the security code.');
    return;
  }

  normalizeCaptchaData(data);
  captchaValue.value = '';
};

const onCaptchaInput = () => {
  captchaError.value = '';
  feedbackMessage.value = '';
};

const onEmailCodeInput = () => {
  emailCodeError.value = '';
  feedbackMessage.value = '';
};

const validateCaptcha = () => {
  if (captchaValue.value.trim() && codeToken.value) {
    captchaError.value = '';
    return true;
  }

  captchaError.value = 'Please enter the security code shown on the right.';
  return false;
};

const validateEmailCode = () => {
  if (emailCode.value.trim()) {
    emailCodeError.value = '';
    return true;
  }

  emailCodeError.value = 'Please enter the Email activation code.';
  return false;
};

const validateEmailForm = async () => {
  const validation = await formRef.value?.validate<UnsubscribeData>();
  if (!validation?.valid || !validation.data?.email) return null;
  return validation.data.email;
};

const onSendCode = async () => {
  if (sendingCode.value) return;

  feedbackMessage.value = '';
  const email = await validateEmailForm();
  if (!email || !validateCaptcha()) return;

  sendingCode.value = true;
  const [, error] = await api.unsubscribe.sendCode(null, {
    email,
    validateCode: captchaValue.value.trim(),
    codeToken: codeToken.value,
  });
  sendingCode.value = false;

  if (error) {
    captchaError.value = getErrorMessage(error, 'Invalid security code.');
    await refreshCaptcha();
    return;
  }

  emailCodeError.value = '';
  feedbackType.value = 'success';
  feedbackMessage.value = 'The Email activation code has been sent.';
};

const onSubmit = async () => {
  if (submitting.value) return;

  feedbackMessage.value = '';
  const email = await validateEmailForm();
  if (!email || !validateEmailCode()) return;

  submitting.value = true;
  const [verifyData, verifyError] = await api.unsubscribe.verifyCode(null, {
    email,
    verificationCode: emailCode.value.trim(),
  });

  if (verifyError) {
    submitting.value = false;
    emailCodeError.value = getErrorMessage(verifyError, 'Invalid Email activation code.');
    return;
  }

  const resetToken = getResetToken(verifyData);
  if (!resetToken) {
    submitting.value = false;
    emailCodeError.value = 'Invalid Email activation code response.';
    return;
  }

  const [, error] = await api.unsubscribe.submit(null, {
    email,
    resetToken: resetToken,
  });
  submitting.value = false;

  if (error) {
    feedbackType.value = 'error';
    feedbackMessage.value = getErrorMessage(
      error,
      fields.value?.errorMessage?.value || 'Submission failed. Please try again.'
    );
    await refreshCaptcha();
    return;
  }

  const successMessage = fields.value?.successMessage?.value
    ? fields.value.successMessage.value.replace(/<[^>]+>/g, '')
    : 'Your unsubscribe request has been submitted.';
  feedbackType.value = 'success';
  feedbackMessage.value = successMessage;
  void toast.showSuccess(successMessage, { duration: 3000 });
  clearTimeout(redirectTimer);
  redirectTimer = setTimeout(() => window.location.assign('/'), 3000);
  formRef.value?.resetFields();
  captchaValue.value = '';
  emailCode.value = '';
  await refreshCaptcha();
};
onMounted(refreshCaptcha);
onBeforeUnmount(() => clearTimeout(redirectTimer));

return (_ctx: any,_cache: any) => {
  const _component_AdaptiveImage = _resolveComponent("AdaptiveImage")!
  const _component_ContentGrid = _resolveComponent("ContentGrid")!

  return (fields.value)
    ? (_openBlock(), _createElementBlock("section", {
        key: 0,
        class: _normalizeClass(rootClass.value),
        style: _normalizeStyle(rootStyle.value),
        id: __props.rendering?.dataSource
      }, [
        (normalizedBackMedia.value)
          ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
              _createVNode(_component_AdaptiveImage, {
                field: normalizedBackMedia.value,
                class: "c-unsubscribe__media-image",
                "background-position": "right bottom",
                "is-background": true,
                "lazy-load": false,
                "enable-scroll-detection": false,
                show: true,
                "show-image-redius": false
              }, null, 8, ["field"])
            ]))
          : _createCommentVNode("", true),
        _createVNode(_component_ContentGrid, {
          "desktop-default-grid": [7, 11],
          "tablet-default-grid": [2, 12],
          "mobile-default-grid": [1, 14]
        }, {
          default: _withCtx(() => [
            _createElementVNode("div", _hoisted_3, [
              (fields.value.title?.value)
                ? (_openBlock(), _createBlock(SiteRichText, {
                    key: 0,
                    class: "c-unsubscribe__title",
                    field: fields.value.title
                  }, null, 8, ["field"]))
                : _createCommentVNode("", true),
              (fields.value.description?.value)
                ? (_openBlock(), _createBlock(SiteRichText, {
                    key: 1,
                    class: "c-unsubscribe__description",
                    field: fields.value.description
                  }, null, 8, ["field"]))
                : _createCommentVNode("", true),
              _createElementVNode("form", {
                class: "c-unsubscribe__form",
                onSubmit: _withModifiers(onSubmit, ["prevent"])
              }, [
                _createElementVNode("div", _hoisted_4, [
                  (fields.value.emailLabel?.value)
                    ? (_openBlock(), _createElementBlock("label", _hoisted_5, _toDisplayString(fields.value.emailLabel.value), 1))
                    : _createCommentVNode("", true),
                  (dynamicForm.value)
                    ? (_openBlock(), _createBlock(DynamicForm, {
                        key: 1,
                        ref_key: "formRef",
                        ref: formRef,
                        form: dynamicForm.value
                      }, null, 8, ["form"]))
                    : _createCommentVNode("", true)
                ]),
                _createElementVNode("div", _hoisted_6, [
                  (fields.value.captchaLabel?.value)
                    ? (_openBlock(), _createElementBlock("label", _hoisted_7, _toDisplayString(fields.value.captchaLabel.value), 1))
                    : _createCommentVNode("", true),
                  _createElementVNode("div", _hoisted_8, [
                    _withDirectives(_createElementVNode("input", {
                      "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((captchaValue).value = $event)),
                      class: _normalizeClass(["c-unsubscribe__captcha-input", { 'has-error': Boolean(captchaError.value) }]),
                      type: "text",
                      inputmode: "text",
                      autocomplete: "off",
                      autocapitalize: "none",
                      spellcheck: "false",
                      "aria-label": "Security code",
                      onInput: onCaptchaInput
                    }, null, 34), [
                      [_vModelText, captchaValue.value]
                    ]),
                    _createElementVNode("img", {
                      class: _normalizeClass(["c-unsubscribe__captcha-image", { 'is-loading': captchaLoading.value }]),
                      src: captchaImage.value,
                      alt: "Security code",
                      role: "button",
                      onClick: refreshCaptcha
                    }, null, 10, _hoisted_9)
                  ]),
                  (captchaError.value)
                    ? (_openBlock(), _createElementBlock("p", _hoisted_10, _toDisplayString(captchaError.value), 1))
                    : _createCommentVNode("", true)
                ]),
                _createElementVNode("div", _hoisted_11, [
                  _createElementVNode("label", _hoisted_12, _toDisplayString(emailCodeLabel.value), 1),
                  _createElementVNode("div", _hoisted_13, [
                    _withDirectives(_createElementVNode("input", {
                      "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((emailCode).value = $event)),
                      class: _normalizeClass(["c-unsubscribe__email-code-input", { 'has-error': Boolean(emailCodeError.value) }]),
                      type: "text",
                      inputmode: "text",
                      autocapitalize: "none",
                      spellcheck: "false",
                      autocomplete: "one-time-code",
                      "aria-label": "Email activation code",
                      onInput: onEmailCodeInput
                    }, null, 34), [
                      [_vModelText, emailCode.value]
                    ]),
                    _createElementVNode("button", {
                      class: "c-unsubscribe__send-code",
                      type: "button",
                      disabled: sendingCode.value,
                      onClick: onSendCode
                    }, _toDisplayString(sendingCode.value ? 'Sending...' : 'Send Code'), 9, _hoisted_14)
                  ]),
                  (emailCodeError.value)
                    ? (_openBlock(), _createElementBlock("p", _hoisted_15, _toDisplayString(emailCodeError.value), 1))
                    : _createCommentVNode("", true)
                ]),
                (feedbackMessage.value)
                  ? (_openBlock(), _createElementBlock("p", {
                      key: 0,
                      class: _normalizeClass(["c-unsubscribe__feedback", feedbackClass.value])
                    }, _toDisplayString(feedbackMessage.value), 3))
                  : _createCommentVNode("", true),
                _createElementVNode("button", {
                  class: "c-unsubscribe__submit",
                  type: "submit",
                  disabled: submitting.value
                }, _toDisplayString(submitting.value ? 'Submitting...' : submitLabel.value), 9, _hoisted_16)
              ], 32),
              (fields.value.fallbackNote?.value)
                ? (_openBlock(), _createBlock(SiteRichText, {
                    key: 2,
                    class: "c-unsubscribe__note",
                    field: fields.value.fallbackNote
                  }, null, 8, ["field"]))
                : _createCommentVNode("", true)
            ])
          ]),
          _: 1
        })
      ], 14, _hoisted_1))
    : _createCommentVNode("", true)
}
}

})